import { Menu, InputV2, ButtonV2, BadgeV3 } from '@cimplico/components';
import { motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { NatureEnum } from '../../../types/nature';
import { LinkIcon } from '@heroicons/react/24/outline';
import { formatCurrencyAmount } from '../../../components/currency/Currency';
import WorksheetOtherScheduleSelect from '../../../components/worksheet-other-schedule-select/WorksheetOtherScheduleSelect';
import { Worksheet, WorksheetEntryTypeEnum, WorksheetTypeEnum } from '../../../types/worksheet';
import { Controller, useForm } from 'react-hook-form';
import { useCreateWorksheetEntry } from '../../../queries/use-create-worksheet-entry';
import queryClient from '../../../query-client';
import { useWorkpaperContext } from '../../../providers/WorkpaperProvider';
import { useParams } from 'react-router-dom';

type LinkEntryFormData = {
    worksheetId: string;
    value: number;
    description: string;
    date: string;
    nature: NatureEnum;
};

type LinkEntryFormProps = {
    description: string | null;
    date: string | null;
    nature: NatureEnum;
    value: number | null;
    name: string;
    workpaperEnd: string;
    calcId: string;
    fieldId: string;
    defaultWorksheet: Worksheet | undefined;
};

function LinkEntryForm({
    description,
    date,
    nature,
    value,
    name,
    workpaperEnd,
    calcId,
    fieldId,
    defaultWorksheet,
}: LinkEntryFormProps): JSX.Element {
    const { id } = useWorkpaperContext();
    const { id: currentWorksheetId } = useParams();
    const ref = useRef<HTMLDivElement | null>(null);
    const formatted = typeof value === 'number' ? formatCurrencyAmount({ value }) : '-';
    const workpaperEndFormatted = new Date(workpaperEnd).toISOString().split('T')[0];

    const form = useForm<LinkEntryFormData>({
        defaultValues: {
            worksheetId: defaultWorksheet ? defaultWorksheet.id : '',
            description: description !== null ? description : name,
            date: date !== null ? date : workpaperEndFormatted,
            nature: nature !== null ? nature : NatureEnum.DEBIT,
            value: value ?? 0,
        },
    });

    useEffect(() => {
        form.setValue('value', value ?? 0);
    }, [form, value]);

    const worksheetId = form.watch('worksheetId');
    const entryQuery = useCreateWorksheetEntry(worksheetId);

    function handleSubmit(data: LinkEntryFormData): void {
        const val = Math.round(Math.abs(data.value ?? 0));

        entryQuery.mutate(
            {
                entry: {
                    amount: val,
                    date: data.date,
                    type: WorksheetEntryTypeEnum.RECONCILIATION_ENTRY,
                    description: data.description,
                    nature: data.nature,
                    source: { type: 'CALCULATION', id: calcId, metadata: { fieldId } },
                },
            },
            {
                onSettled: () => {
                    queryClient.invalidateQueries(['workpapers', id, 'calculations', calcId]);
                    queryClient.invalidateQueries(['worksheets']);
                },
            }
        );
    }
    return (
        <Menu>
            <Menu.Button>
                <LinkIcon className="h-4 w-4" />
            </Menu.Button>
            <Menu.Body>
                <div className="w-[350px] min-h-[450px] p-4" ref={ref}>
                    <form onSubmit={form.handleSubmit(handleSubmit)}>
                        <div className="mb-2 flex items-center gap-x-2  ">
                            <p className=" text-gray-800 font-medium">Select a worksheet </p>
                            {worksheetId === currentWorksheetId ? <BadgeV3 color="indigo" label="current" /> : <></>}
                        </div>

                        {/* Worksheet */}
                        <Controller
                            name="worksheetId"
                            control={form.control}
                            rules={{ required: 'A worksheet is required.' }}
                            render={({ field, fieldState }): JSX.Element => (
                                <WorksheetOtherScheduleSelect
                                    isMulti={false}
                                    onChange={(e): void => field.onChange(e?.at(0)?.id ?? '')}
                                    placeholder="Select a worksheet"
                                    selectedIds={field.value ? [field.value] : []}
                                    errorMessage={fieldState.error?.message}
                                    type={WorksheetTypeEnum.RECONCILIATION}
                                />
                            )}
                        />
                        <p className=" text-gray-800 font-medium my-2 border-t pt-2">Entry details</p>

                        {/* Value */}
                        <div className="mb-2">
                            <InputV2 type="text" disabled title="Value" defaultValue={formatted} />
                        </div>

                        {/* Description */}
                        <Controller
                            name="description"
                            control={form.control}
                            // rules={{ required: false }}
                            render={(render): JSX.Element => (
                                <div className="mb-2">
                                    <InputV2
                                        type="text"
                                        title="Description"
                                        defaultValue={render.field.value}
                                        onChange={(e): void => render.field.onChange(e)}
                                    />
                                </div>
                            )}
                        />

                        {/* Date */}
                        <Controller
                            name="date"
                            control={form.control}
                            render={({ field }): JSX.Element => (
                                <InputV2 type="date" title="Date" defaultValue={field.value} onChange={field.onChange} />
                            )}
                        />

                        {/* Nature Credit/Debit */}
                        <Controller
                            name="nature"
                            control={form.control}
                            render={({ field }): JSX.Element => {
                                return (
                                    <div>
                                        <p className="block text-gray-700 dark:text-gray-200 font-medium text-sm mt-2 mb-1">
                                            Positive Nature
                                        </p>
                                        <NatureSelector selectedNature={field.value} onChange={field.onChange} />
                                    </div>
                                );
                            }}
                        />

                        <div className="w-full flex flex-col mt-4">
                            <ButtonV2
                                label={entryQuery.isLoading ? 'Creating...' : 'Create entry'}
                                size="sm"
                                isSubmit
                                isLoading={entryQuery.isLoading}
                                isDisabled={entryQuery.isLoading}
                            />
                        </div>
                    </form>
                </div>
            </Menu.Body>
        </Menu>
    );
}

export default LinkEntryForm;

type NatureSelectorProps = {
    selectedNature: NatureEnum;
    onChange: (value: NatureEnum) => void;
};

function NatureSelector({ selectedNature, onChange }: NatureSelectorProps): JSX.Element {
    return (
        <div className="h-fit flex items-center gap-2 p-1 bg-gray-100 rounded-md w-fit">
            {Object.values(NatureEnum)
                .sort((a, b) => (a > b ? -1 : 1))
                .map((n) => {
                    const isSelected = n === selectedNature;
                    return (
                        <div key={n} className="py-1 px-3 relative" onClick={() => onChange(n)}>
                            <div className="w-4 h-4 flex justify-center items-center shrink-0">
                                {isSelected && (
                                    <motion.div
                                        initial={false}
                                        layoutId="entry-nature"
                                        className="w-full h-full rounded-md bg-white absolute left-0"
                                    />
                                )}
                                <div
                                    className={`z-10 hover:cursor-pointer hover:text-gray-800 text-sm ${
                                        isSelected ? 'text-gray-800' : 'text-gray-400'
                                    }`}
                                >
                                    {`${n[0]}r`}
                                </div>
                            </div>
                        </div>
                    );
                })}
        </div>
    );
}
